/**
 * Sessions table (WS-5.2): number, date, modality, duration, source, SOF status,
 * include-in-CRF toggle, and the per-row SOF actions.
 *
 * Under 600 px the table becomes stacked label/value cards (no horizontal
 * scroll); every `engagement_session_*` testID is kept in both layouts.
 */
import React, { useState } from 'react';
import { Switch, Text, View } from 'react-native';

import { useResponsive } from '@/hooks/useResponsive';
import { SessionPractice } from './SessionPractice';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { InfoHint } from '@/components/tutorial/InfoHint';
import type { EngagementLevel, EngagementSessionRow, SessionUpdateRequest } from '@/types/mentorEngagements';
import { ActionButton, Chip, Panel, mentorStyles } from './shared';
import { levelName } from './levelAnalysis';

interface SessionsTableProps {
  sessions: EngagementSessionRow[];
  /** Engagement target level, named in the "needs analysis" label (B2B-9). */
  targetLevel?: EngagementLevel;
  practiceSession?: number;
  onSavePractice?: (session: EngagementSessionRow, payload: SessionUpdateRequest) => Promise<void>;
  readOnly?: boolean;
  onToggleInclude: (session: EngagementSessionRow, include: boolean) => void;
  onDownloadSof: (session: EngagementSessionRow) => void;
  onFinalizeSof: (session: EngagementSessionRow) => void;
  onReopenSof: (session: EngagementSessionRow) => void;
  onReview: (session: EngagementSessionRow) => void;
  /** Manual sessions: edit the hand-entered SOF grid + metadata. */
  onEdit?: (session: EngagementSessionRow) => void;
  /** Any session: edit date / modality / duration only. */
  onEditMeta?: (session: EngagementSessionRow) => void;
  onRemove: (session: EngagementSessionRow) => void;
  onUploadSession: () => void;
  onAddManual: () => void;
}

const COLUMN_WIDTHS = {
  number: 44,
  date: 110,
  modality: 110,
  duration: 90,
  source: 180,
  sof: 120,
  include: 76,
  actions: 300,
};

export function SessionsTable({
  sessions,
  targetLevel,
  practiceSession,
  onSavePractice,
  readOnly = false,
  onToggleInclude,
  onDownloadSof,
  onFinalizeSof,
  onReopenSof,
  onRemove,
  onEdit,
  onEditMeta,
  onReview,
  onUploadSession,
  onAddManual,
}: SessionsTableProps) {
  const { t } = useTranslation();
  const { isDesktop, isPhone } = useResponsive();
  const compact = !isDesktop;
  const [expandedActions, setExpandedActions] = useState<Record<number, boolean>>({});

  const header = (
    <View style={[mentorStyles.tableHeader, { flexWrap: 'wrap' }]}>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.number }]}>{t('mentorEngagements.sessions.number')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.date }]}>{t('mentorEngagements.sessions.date')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.modality }]}>{t('mentorEngagements.sessions.modality')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.duration }]}>{t('mentorEngagements.sessions.duration')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.source }]}>{t('mentorEngagements.sessions.source')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.sof }]}>{t('mentorEngagements.sessions.sofStatus')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.include }]}>{t('mentorEngagements.sessions.includeInCrf')}</Text>
      <Text style={[mentorStyles.tableHeaderCell, { width: COLUMN_WIDTHS.actions }]}>{t('mentorEngagements.sessions.actions')}</Text>
    </View>
  );

  const dateText = (session: EngagementSessionRow) => session.session_date || '—';
  const modalityText = (session: EngagementSessionRow) =>
    session.modality ? t(`mentorEngagements.modality.${session.modality}`) : '—';
  const durationText = (session: EngagementSessionRow) =>
    session.duration_minutes ? t('mentorEngagements.sessions.minutes', { count: session.duration_minutes }) : '—';

  const levelChip = (session: EngagementSessionRow) => (
    session.needs_level_analysis && targetLevel ? (
      <View testID={`engagement_session_needs_level_${session.id}`}>
        <Chip
          tone="warning"
          label={t('mentorEngagements.sessions.needsLevelAnalysis', { level: levelName(targetLevel) })}
        />
      </View>
    ) : null
  );

  const sofChip = (session: EngagementSessionRow) => (
    <View style={{ gap: 4, alignItems: 'flex-start' }}>
      {session.sof_status ? (
        <Chip
          label={t(`mentorEngagements.formStatus.${session.sof_status}`)}
          tone={session.sof_status === 'final' ? 'success' : 'neutral'}
        />
      ) : (
        <Text style={mentorStyles.mutedText}>{t('mentorEngagements.sessions.notStarted')}</Text>
      )}
      {levelChip(session)}
    </View>
  );

  const includeSwitch = (session: EngagementSessionRow) => (
    <Switch
      testID={`engagement_session_include_${session.id}`}
      value={session.include_in_crf}
      onValueChange={(value) => onToggleInclude(session, value)}
      disabled={readOnly}
      accessibilityLabel={t('mentorEngagements.sessions.includeInCrf')}
      trackColor={{ false: reviewDesk.line, true: reviewDesk.accentSoft }}
      thumbColor={session.include_in_crf ? reviewDesk.accent : reviewDesk.muted}
    />
  );

  const actions = (session: EngagementSessionRow) => (
    <View style={{ width: compact ? '100%' : COLUMN_WIDTHS.actions, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {session.sof_status ? <ActionButton
        testID={`engagement_review_session_${session.id}`}
        label={t('mentorEngagements.review.action')}
        icon="rate-review"
        onPress={() => onReview(session)}
      /> : null}
      <ActionButton testID={`engagement_session_more_${session.id}`} label={t('mentorEngagements.workspace.sessionActions')} icon={expandedActions[session.id] ? 'expand-less' : 'expand-more'} onPress={() => setExpandedActions(current => ({ ...current, [session.id]: !current[session.id] }))} />
      {expandedActions[session.id] ? <>
      {session.source === 'manual' && session.sof_status !== 'final' && onEdit ? (
        <ActionButton
          testID={`engagement_session_edit_${session.id}`}
          label={t('mentorEngagements.sessions.editSof')}
          icon="edit"
          onPress={() => onEdit(session)}
          disabled={readOnly}
        />
      ) : null}
      {onEditMeta && session.sof_status !== 'final' ? (
        <ActionButton
          testID={`engagement_session_edit_meta_${session.id}`}
          label={t('mentorEngagements.sessionMeta.editAction')}
          icon="event"
          onPress={() => onEditMeta(session)}
          disabled={readOnly}
        />
      ) : null}
      <ActionButton
        label={t('mentorEngagements.sessions.downloadSof')}
        icon="picture-as-pdf"
        onPress={() => onDownloadSof(session)}
        disabled={!session.sof_status}
      />
      {session.sof_status === 'final' ? (
        <ActionButton
          label={t('mentorEngagements.sessions.reopen')}
          icon="lock-open"
          onPress={() => onReopenSof(session)}
          disabled={readOnly}
        />
      ) : (
        <ActionButton
          label={t('mentorEngagements.sessions.finalize')}
          icon="verified"
          onPress={() => onFinalizeSof(session)}
          disabled={readOnly || !session.sof_status}
        />
      )}
      <ActionButton
        label={t('mentorEngagements.sessions.remove')}
        icon="delete-outline"
        variant="danger"
        onPress={() => onRemove(session)}
        // A signed SOF can't be deleted (server returns 409 sof_locked); reopen first.
        disabled={readOnly || session.sof_status === 'final'}
      />
      </> : null}
    </View>
  );

  const renderRow = (session: EngagementSessionRow) => (
    <View key={session.id} testID={`engagement_session_row_${session.id}`} style={[mentorStyles.tableRow, { flexWrap: 'wrap', alignItems: 'flex-start' }]}>
      <Text style={[mentorStyles.tableCell, { width: compact ? 100 : COLUMN_WIDTHS.number, fontWeight: '800', color: reviewDesk.ink }]}>
        {compact ? t('mentorEngagements.workspace.sessionNumber', { number: session.session_number }) : session.session_number}
      </Text>
      <Text style={[mentorStyles.tableCell, { width: COLUMN_WIDTHS.date }]}>{dateText(session)}</Text>
      <Text style={[mentorStyles.tableCell, { width: COLUMN_WIDTHS.modality }]}>{modalityText(session)}</Text>
      <Text style={[mentorStyles.tableCell, { width: COLUMN_WIDTHS.duration }]}>{durationText(session)}</Text>
      <View style={{ width: COLUMN_WIDTHS.source }}>
        <Chip label={t(`mentorEngagements.review.${session.source === 'mcai' ? 'uploaded' : 'manual'}`)} />
      </View>
      <View style={{ width: COLUMN_WIDTHS.sof }}>{sofChip(session)}</View>
      <View style={{ width: COLUMN_WIDTHS.include }}>{includeSwitch(session)}</View>
      {actions(session)}
    </View>
  );

  // Phone layout: one stacked card per session, label/value pairs, no fixed widths.
  const renderCard = (session: EngagementSessionRow) => {
    const pair = (label: string, value: React.ReactNode) => (
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <Text style={mentorStyles.fieldLabel}>{label}</Text>
        {typeof value === 'string' ? <Text style={[mentorStyles.tableCell, { flexShrink: 1, textAlign: 'right' }]}>{value}</Text> : value}
      </View>
    );
    return (
      <View
        key={session.id}
        testID={`engagement_session_row_${session.id}`}
        style={{ gap: 8, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: reviewDesk.line }}
      >
        <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
          <Text style={[mentorStyles.tableCell, { fontWeight: '800', color: reviewDesk.ink, fontSize: 14 }]}>
            {t('mentorEngagements.workspace.sessionNumber', { number: session.session_number })}
          </Text>
          <Chip label={t(`mentorEngagements.review.${session.source === 'mcai' ? 'uploaded' : 'manual'}`)} />
        </View>
        {pair(t('mentorEngagements.sessions.date'), dateText(session))}
        {pair(t('mentorEngagements.sessions.modality'), modalityText(session))}
        {pair(t('mentorEngagements.sessions.duration'), durationText(session))}
        {pair(t('mentorEngagements.sessions.sofStatus'), sofChip(session))}
        {pair(t('mentorEngagements.sessions.includeInCrf'), includeSwitch(session))}
        {actions(session)}
      </View>
    );
  };

  return (
    <Panel testID="engagement_sessions_panel">
      <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.sessions.title')}</Text>
        <View style={mentorStyles.row}>
          <ActionButton
            testID="engagement_upload_session"
            label={t('mentorEngagements.sessions.uploadSession')}
            icon="upload-file"
            variant="primary"
            onPress={onUploadSession}
            disabled={readOnly}
          />
          <ActionButton
            testID="engagement_add_manual_session"
            label={t('mentorEngagements.sessions.addManual')}
            icon="edit-note"
            onPress={onAddManual}
            disabled={readOnly}
          />
        </View>
      </View>

      {sessions.length > 0 && <InfoHint id="sof_review" />}
      {sessions.length === 0 ? (
        <Text testID="engagement_sessions_empty" style={mentorStyles.mutedText}>{t('mentorEngagements.sessions.empty')}</Text>
      ) : (
        <View>
          {!compact ? header : null}
          {sessions.map(session => <View key={session.id}>
            {isPhone ? renderCard(session) : renderRow(session)}
            {onSavePractice ? <SessionPractice key={`${session.id}-${practiceSession === session.id}`} session={session} readOnly={readOnly} initiallyExpanded={practiceSession === session.id} onSave={payload => onSavePractice(session, payload)} /> : null}
          </View>)}
        </View>
      )}
    </Panel>
  );
}

export default SessionsTable;
